import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import { isAuthenticated, useRegister } from '../hooks/useAuth';
import logoRw from '../logo-rw.png';

const registerSchema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
    role: z.enum(['PROVINCE_ADMIN', 'DISTRICT_ADMIN', 'SECTOR_ADMIN', 'CELL_ADMIN', 'VILLAGE_ADMIN']),
    geoLocationId: z.string().min(1, 'Please select a location'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterForm = z.infer<typeof registerSchema>;

export default function Register() {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1: User's role
  const [selectedRole, setSelectedRole] = useState<string>('');

  // Step 2: Location based on role
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const registerMutation = useRegister();
  const { dictionary } = useLocale();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      geoLocationId: '',
      role: undefined,
    },
  });

  if (isAuthenticated()) {
    return <Navigate to='/facilities' replace />;
  }

  const onSubmit = async (data: RegisterForm) => {
    const { confirmPassword, ...registerData } = data;

    // Validate that we have a location selected
    if (!registerData.geoLocationId) {
      toast.error(dictionary['register.select_location']);
      return;
    }

    try {
      await registerMutation.mutateAsync({
        name: registerData.name!,
        email: registerData.email!,
        password: registerData.password!,
        // role: registerData.role!,
        geoLocationId: registerData.geoLocationId!,
      });

      // Navigate to login page after successful registration
      toast.success(dictionary['register.success'] || 'Registration successful! Please login.');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      // Error is already handled by the mutation
      console.error('Registration error:', error);
    }
  };

  // Map role to required location level
  const getRoleLocationLevel = (role: string): 'province' | 'district' | 'sector' | 'cell' | 'village' | undefined => {
    const roleToLevel: Record<string, 'province' | 'district' | 'sector' | 'cell' | 'village'> = {
      PROVINCE_ADMIN: 'province',
      DISTRICT_ADMIN: 'district',
      SECTOR_ADMIN: 'sector',
      CELL_ADMIN: 'cell',
      VILLAGE_ADMIN: 'village',
    };

    return roleToLevel[role];
  };

  const handleRoleChange = (role: string) => {
    // If role changes, reset location selection
    if (role !== selectedRole) {
      setSelectedLocationId(undefined);
      setValue('geoLocationId', '');
    }
    setSelectedRole(role);
    setValue('role', role as any);
  };

  const handleLocationChange = (locationId: string | undefined) => {
    setSelectedLocationId(locationId);
    setValue('geoLocationId', locationId || '');
  };

  const handleNextStep = () => {
    if (currentStep === 1 && selectedRole) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4'>
      <div className='max-w-md w-full'>
        <div className='bg-white rounded-lg  p-8'>
          <div className='text-center mb-8'>
            <div className='flex justify-center mb-4'>
              <img src={logoRw} alt='Republic of Rwanda Logo' className='h-16 w-16' />
            </div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>{dictionary['register.title']}</h1>
            <p className='text-gray-600'>{dictionary['register.subtitle']}</p>

            {/* Step indicator */}
            <div className='flex items-center justify-center mt-6 space-x-2'>
              <div className={`flex items-center ${currentStep === 1 ? 'text-primary' : 'text-gray-400'}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 1 ? 'bg-primary text-white' : 'bg-gray-200'}`}
                >
                  1
                </div>
                <span className='ml-2 text-sm font-medium'>Role & Info</span>
              </div>
              <div className='w-8 h-0.5 bg-gray-300'></div>
              <div className={`flex items-center ${currentStep === 2 ? 'text-primary' : 'text-gray-400'}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 2 ? 'bg-primary text-white' : 'bg-gray-200'}`}
                >
                  2
                </div>
                <span className='ml-2 text-sm font-medium'>Location</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            {currentStep === 1 ? (
              <>
                {/* Step 1: Basic info and role selection */}
                <div className='space-y-4'>
                  <div className='mb-4'>
                    <h2 className='text-lg font-semibold text-gray-800 mb-2'>Basic Information</h2>
                  </div>

                  <div>
                    <Input
                      type='text'
                      placeholder={dictionary['register.full_name']}
                      error={errors.name?.message}
                      {...register('name')}
                    />
                  </div>

                  <div>
                    <Input
                      type='email'
                      placeholder={dictionary['auth.email']}
                      autoComplete='email'
                      error={errors.email?.message}
                      {...register('email')}
                    />
                  </div>

                  <div>
                    <Input
                      type='password'
                      placeholder={dictionary['auth.password']}
                      autoComplete='new-password'
                      error={errors.password?.message}
                      {...register('password')}
                    />
                  </div>

                  <div>
                    <Input
                      type='password'
                      placeholder={dictionary['register.confirm_password']}
                      autoComplete='new-password'
                      error={errors.confirmPassword?.message}
                      {...register('confirmPassword')}
                    />
                  </div>

                  <div className='pt-4 border-t border-gray-200'>
                    <h2 className='text-lg font-semibold text-gray-800 mb-2'>Administrative Role</h2>
                    <p className='text-sm text-gray-600 mb-3'>Select the administrative level you represent</p>
                    <Input
                      type='select'
                      value={selectedRole}
                      onChange={e => handleRoleChange(e.target.value as string)}
                      placeholder={dictionary['register.select_role']}
                      options={[
                        { value: 'PROVINCE_ADMIN', label: dictionary['roles.province_admin'] },
                        { value: 'DISTRICT_ADMIN', label: dictionary['roles.district_admin'] },
                        { value: 'SECTOR_ADMIN', label: dictionary['roles.sector_admin'] },
                        { value: 'CELL_ADMIN', label: dictionary['roles.cell_admin'] },
                        { value: 'VILLAGE_ADMIN', label: dictionary['roles.village_admin'] },
                      ]}
                      error={errors.role?.message}
                    />

                    {selectedRole && (
                      <div className='mt-3 p-3 bg-secondary border border-primary/30 rounded-md'>
                        <p className='text-sm text-primary'>
                          <strong>Note:</strong> You will select your {getRoleLocationLevel(selectedRole)} in the next
                          step.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type='button'
                  onClick={handleNextStep}
                  disabled={!selectedRole}
                  className='w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next: Select Location
                </button>
              </>
            ) : (
              <>
                {/* Step 2: Location selection based on role */}
                <div className='space-y-4'>
                  <div className='mb-4'>
                    <h2 className='text-lg font-semibold text-gray-800 mb-2'>Select Your Administrative Location</h2>
                    <p className='text-sm text-gray-600'>
                      As a <strong className='capitalize'>{selectedRole.replace('_', ' ').toLowerCase()}</strong>, you
                      must select your administrative {getRoleLocationLevel(selectedRole)}.
                    </p>
                    <div className='mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md'>
                      <p className='text-sm text-amber-800'>
                        <strong>Important:</strong> Please select all levels up to and including your{' '}
                        <strong className='capitalize'>{getRoleLocationLevel(selectedRole)}</strong>.
                      </p>
                    </div>
                  </div>

                  <div>
                    <SingleLocationSelector
                      selectedLocationId={selectedLocationId}
                      onLocationChange={handleLocationChange}
                      minimumLevel={getRoleLocationLevel(selectedRole)}
                      maximumLevel={getRoleLocationLevel(selectedRole)}
                    />
                    {errors.geoLocationId && (
                      <p className='mt-1 text-sm text-red-600'>{errors.geoLocationId.message}</p>
                    )}
                  </div>
                </div>

                {/* Hidden inputs to ensure all fields are registered */}
                <input type='hidden' {...register('geoLocationId')} />
                <input type='hidden' {...register('role')} />

                <div className='flex space-x-3'>
                  <button
                    type='button'
                    onClick={handlePreviousStep}
                    className='flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300'
                  >
                    Back
                  </button>
                  <button
                    type='submit'
                    disabled={isSubmitting || registerMutation.isPending || !selectedLocationId}
                    className='flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {isSubmitting || registerMutation.isPending
                      ? dictionary['register.creating_account']
                      : dictionary['register.create_account']}
                  </button>
                </div>
              </>
            )}
          </form>

          <div className='mt-4 text-center'>
            <p className='text-sm text-gray-600'>
              {dictionary['register.already_have_account']}{' '}
              <Link to='/login' className='text-primary hover:text-primary/80 font-medium'>
                {dictionary['register.sign_in']}
              </Link>
            </p>
          </div>

          {registerMutation.isSuccess && (
            <div className='mt-4 p-3 bg-green-50 border border-green-200 rounded-md'>
              <p className='text-sm text-green-800'>{dictionary['register.success']}</p>
            </div>
          )}

          {registerMutation.isError && (
            <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-md'>
              <p className='text-sm text-red-800'>{dictionary['register.error']}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
